import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Row, Col, Image, Descriptions, Tag, message } from 'antd';
import ModalItem from './ModalItem';

// Ficha de solo lectura del insumo (datos + imagenes), abierta con el ojo de Acciones en los
// renglones de Ventas/Pedidos/Cotizaciones/Embarques. Sin costos ni precios: eso es VerInsumo.
const IMAGEN_RESPALDO = '/Resources/logoBiwoInsumos.png';

const formatearValor = ({ tipoDato, valor, unidad }) => {
  let texto;
  if (tipoDato === 'booleano') texto = valor === true || valor === 'true' ? 'Sí' : 'No';
  else if (tipoDato === 'fecha') texto = moment(valor).isValid() ? moment(valor).format('DD/MM/YYYY') : String(valor);
  else texto = String(valor);
  return unidad ? `${texto} ${unidad}` : texto;
};

const DetalleInsumoModal = ({ visible, onClose, insumoId, nombreInsumo, headersToken, empresaId }) => {
  const [loading, setLoading] = useState(false);
  const [insumo, setInsumo] = useState(null);
  const [imagenActual, setImagenActual] = useState(0);

  useEffect(() => {
    if (visible && insumoId) {
      consultarDetalle();
    } else if (!visible) {
      setInsumo(null);
      setImagenActual(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, insumoId]);

  const consultarDetalle = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        'insumos/detalleInsumo',
        { empresaId, insumoId },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        setInsumo(res.data.insumo);
        setImagenActual(0);
      } else {
        message.warning(res.data.message || 'No se pudo cargar el detalle del insumo');
      }
    } catch {
      message.warning('No se pudo cargar el detalle del insumo');
    } finally {
      setLoading(false);
    }
  };

  const tituloSeccion = (texto) => (
    <div style={{
      fontSize: 11,
      fontWeight: 700,
      color: '#00b5e2',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      margin: '14px 0 6px 2px',
    }}>
      {texto}
    </div>
  );

  const valorOGuion = (valor) => (valor || valor === 0 ? valor : <span style={{ color: '#9ca3af' }}>—</span>);

  const renderImagenes = () => {
    const imagenes = insumo?.imagenes || [];

    if (imagenes.length === 0) {
      return (
        <div style={{ textAlign: 'center', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}>
          <img src={IMAGEN_RESPALDO} alt="Sin imagen" style={{ maxWidth: '100%', maxHeight: 220, opacity: 0.5 }} />
          <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 6 }}>Sin imagen</div>
        </div>
      );
    }

    // La vista previa (zoom y flechas) recorre todas las imagenes del grupo. La grande es una
    // de ellas; las miniaturas solo cambian cual se ve grande.
    return (
      <Image.PreviewGroup>
        <div style={{
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          height: 280,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: '#fafafa',
        }}>
          {imagenes.map((src, i) => (
            <div key={src + i} style={{ display: i === imagenActual ? 'block' : 'none' }}>
              <Image
                src={src}
                fallback={IMAGEN_RESPALDO}
                style={{ maxWidth: '100%', maxHeight: 270, objectFit: 'contain' }}
              />
            </div>
          ))}
        </div>
        {imagenes.length > 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {imagenes.map((src, i) => (
              <img
                key={src + i}
                src={src}
                alt={`Imagen ${i + 1}`}
                onClick={() => setImagenActual(i)}
                onError={(e) => { e.currentTarget.src = IMAGEN_RESPALDO; }}
                style={{
                  width: 56,
                  height: 56,
                  objectFit: 'cover',
                  borderRadius: 6,
                  cursor: 'pointer',
                  border: i === imagenActual ? '2px solid #00b5e2' : '1px solid #e5e7eb',
                }}
              />
            ))}
          </div>
        )}
      </Image.PreviewGroup>
    );
  };

  const renderDatos = () => (
    <>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 8, wordBreak: 'break-word' }}>
        {insumo?.descripcion}
      </div>
      {insumo?.esEquivalencia && (
        <div style={{ marginBottom: 8 }}>
          <Tag color="purple">
            Equivalencia: 1 {insumo.unidad || ''} = {insumo.factorEquivalencia} {insumo.unidadPrincipal || ''}
          </Tag>
          {insumo.descripcionPrincipal && (
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              Insumo principal: {insumo.descripcionPrincipal}
            </div>
          )}
        </div>
      )}
      <Descriptions size="small" bordered column={1} labelStyle={{ width: 140 }}>
        <Descriptions.Item label="Clave">{valorOGuion(insumo?.clave)}</Descriptions.Item>
        <Descriptions.Item label="Código">{valorOGuion(insumo?.codigo)}</Descriptions.Item>
        <Descriptions.Item label="Código de barras">{valorOGuion(insumo?.codigoBarras)}</Descriptions.Item>
        <Descriptions.Item label="Unidad">{valorOGuion(insumo?.unidad)}</Descriptions.Item>
        <Descriptions.Item label="Marca">{valorOGuion(insumo?.marca)}</Descriptions.Item>
        <Descriptions.Item label="Familia">{valorOGuion(insumo?.familia)}</Descriptions.Item>
        <Descriptions.Item label="Tipo">{valorOGuion(insumo?.tipo)}</Descriptions.Item>
      </Descriptions>
    </>
  );

  return (
    <ModalItem
      title={`Detalle — ${nombreInsumo || insumo?.descripcion || ''}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={820}
      spinning={loading}
      destroyOnClose
      paddingTop
    >
      {insumo ? (
        <div style={{ paddingBottom: 12 }}>
          <Row gutter={16}>
            <Col xs={24} md={10}>{renderImagenes()}</Col>
            <Col xs={24} md={14}>{renderDatos()}</Col>
          </Row>

          {insumo.descripcionLarga && (
            <>
              {tituloSeccion('Descripción larga')}
              <div style={{
                whiteSpace: 'pre-wrap',
                fontSize: 12,
                color: '#374151',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                padding: '8px 10px',
                maxHeight: 180,
                overflowY: 'auto',
              }}>
                {insumo.descripcionLarga}
              </div>
            </>
          )}

          {insumo.caracteristicas?.length > 0 && (
            <>
              {tituloSeccion('Características')}
              <Descriptions size="small" bordered column={2}>
                {insumo.caracteristicas.map((c, i) => (
                  <Descriptions.Item key={c.categoria + i} label={c.categoria}>
                    {formatearValor(c)}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </>
          )}
        </div>
      ) : (
        <div style={{ minHeight: 120 }} />
      )}
    </ModalItem>
  );
};

export default DetalleInsumoModal;
